import { useEffect, useMemo, useRef, useState } from "react";
import {
  usePhoneInput,
  defaultCountries,
  CountryIso2,
  CountryData,
  FlagImage,
} from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { ChevronDown, Search } from "lucide-react";

type AuthPhoneInputProps = {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  defaultCountry?: CountryIso2;
  countries?: CountryData[];
  placeholder?: string;
  onValidityChange?: (isValid: boolean) => void;
};

const isOnlyCountryCode = (value?: string) => {
  if (!value) return true;
  const cleaned = value.replace(/\s+/g, "");
  return /^\+\d{1,3}$/.test(cleaned);
};

const normalizeValue = (v?: string) => {
  if (!v) return "";
  return isOnlyCountryCode(v) ? "" : v;
};

const AuthPhoneInput = ({
  name,
  label,
  value = "",
  onChange,
  error,
  defaultCountry = "in",
  countries = defaultCountries,
  placeholder = "Enter your phone number",
  onValidityChange,
}: AuthPhoneInputProps) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // close dropdown on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filteredCountries = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return countries;

    return countries.filter((c) => {
      const name = String(c[0] ?? "").toLowerCase();
      const iso2 = String(c[1] ?? "").toLowerCase();
      const dial = String(c[2] ?? "");
      const qq = query.replace("+", "");
      return name.includes(query) || iso2.includes(query) || dial.includes(qq);
    });
  }, [q, countries]);

  const {
    phone,
    country,
    handlePhoneValueChange,
    setCountry,
  } = usePhoneInput({
    value: normalizeValue(value),
    defaultCountry,
    countries,
    forceDialCode: false, // important
    onChange: (data) => {
      const next = normalizeValue(data.phone);
      onChange(next);

      if (onValidityChange) {
        const ok = next ? isValidPhoneNumber(next) : false;
        onValidityChange(ok);
      }
    },
  });

  // 🔥 remove dial code from visible input only
  const visibleNumber = phone
    ? phone.replace(`+${country.dialCode}`, "").trim()
    : "";

  return (
    <div className="space-y-2" ref={wrapRef}>
      <label className="text-[#121217] text-sm tracking-normal font-medium">
        {label}
      </label>

      <div className="flex items-center gap-x-2 relative">
        {/* LEFT: FLAG + CODE */}
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className={`
            auth-input-shadow
            px-2
            py-[11px]
            rounded-lg
            flex
            items-center
            gap-2
            select-none
            ${error ? "border border-red-500" : ""}
          `}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <FlagImage
            iso2={country.iso2}
            className="w-[36px] h-[19px] rounded"
          />
          <p className="text-[#121217] text-sm tracking-normal font-medium">
            +{country.dialCode}
          </p>
          <ChevronDown color="#98A2B3" size={15} />
        </button>

        {/* RIGHT: PHONE INPUT */}
        <input
          id={name}
          name={name}
          type="tel"
          value={visibleNumber}
          onChange={handlePhoneValueChange}
          placeholder={placeholder}
          autoComplete="tel"
          className={`
            py-[12.5px]
            px-4
            text-sm
            rounded-lg
            w-full
            outline-none
            bg-white
            appearance-none
            focus:outline-none
            focus:ring-2
            focus:ring-[#FF6600]
            transition-all
            duration-200
            ${
              error
                ? "border border-red-500 focus:ring-red-500"
                : "border border-[#E4E7EC]"
            }
          `}
          style={{
            boxShadow:
              "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            WebkitBoxShadow:
              "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            backgroundClip: "padding-box",
            WebkitAppearance: "none",
          }}
        />

        {/* DROPDOWN */}
        {open && (
          <div
            className="absolute z-50 top-[52px] left-0 w-[320px] bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            role="listbox"
          >
            <div className="p-2 border-b border-gray-100">
              <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-gray-50">
                <Search size={16} className="text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search country or code"
                  className="w-full bg-transparent outline-none text-sm text-gray-700"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-[280px] overflow-y-auto">
              {filteredCountries.map((c) => {
                const cname = String(c[0]);
                const iso2 = c[1] as CountryIso2;
                const dial = String(c[2]);
                const active = iso2 === country.iso2;

                return (
                  <button
                    key={`${iso2}-${dial}-${cname}`}
                    type="button"
                    onClick={() => {
                      setCountry(iso2);
                      setOpen(false);
                      setQ("");
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 ${
                      active ? "bg-gray-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FlagImage
                        iso2={iso2}
                        className="w-[28px] h-[16px] rounded"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm text-[#121217] font-medium leading-5">
                          {cname}
                        </span>
                        <span className="text-xs text-gray-500">
                          {iso2.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <span className="text-sm text-gray-700 font-medium">
                      +{dial}
                    </span>
                  </button>
                );
              })}

              {filteredCountries.length === 0 && (
                <div className="p-3 text-sm text-gray-500">
                  No countries found.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default AuthPhoneInput;